import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ArrowLeft, Shield, CheckCircle, AlertCircle, FileText, ExternalLink, User, Mail, Phone, MessageSquare, DollarSign, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { businessInfo } from '../mock';

// SEO metadata for this page (set on mount, restored on unmount)
const PAGE_TITLE = 'FFL Transfer Services in Moncks Corner, SC | TayLord Arms LLC';
const PAGE_DESCRIPTION = 'TayLord Arms LLC provides licensed FFL transfer services in Moncks Corner, SC alongside professional gunsmithing and firearm support services.';

const usePageSeo = () => {
  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content') || '';

    document.title = PAGE_TITLE;
    if (metaDesc) metaDesc.setAttribute('content', PAGE_DESCRIPTION);

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute('content', prevDesc);
    };
  }, []);
};

const FAQ_ITEMS = [
  {
    q: 'How long does an FFL transfer take?',
    a: 'Once your firearm arrives at our shop, we typically notify you within one business day. Pickup itself takes about 15–20 minutes once you complete ATF Form 4473 and the background check clears.',
  },
  {
    q: 'What do I need to bring for pickup?',
    a: 'A valid government-issued photo ID showing your South Carolina address (or active-duty military ID with orders showing assignment in SC). You must be 21 years of age or older.',
  },
  {
    q: 'Can I transfer firearms purchased online?',
    a: 'Yes — that\'s one of the most common transfers we handle. Send us your name and order confirmation, we\'ll coordinate with the seller, and notify you the moment your firearm is ready for pickup.',
  },
];

const FFL = () => {
  usePageSeo();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    category: 'FFL Transfer'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Message sent successfully!', {
          description: 'We\'ll get back to you within 24 hours regarding your FFL transfer inquiry.'
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          category: 'FFL Transfer'
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message', {
        description: 'Please try again or call us directly at ' + businessInfo.phone
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/">
            <Button variant="outline" className="mb-6 bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <Shield className="w-12 h-12 text-gray-300 mr-3" />
              <Badge className="bg-red-600/20 text-red-400 border-red-600/30 px-4 py-2 text-base">
                Type 01 FFL Dealer
              </Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              FFL Transfer Services in Moncks Corner, SC
            </h1>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              TayLord Arms LLC is a full-service gunsmithing and firearm services business in Moncks Corner, South Carolina. As a licensed Type 01 FFL dealer, we also provide professional FFL transfers for online firearm purchases and private-party dealer transfers — every step handled legally, respectfully, and with care.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Legal Requirements */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-2xl flex items-center">
                <AlertCircle className="w-6 h-6 text-gray-300 mr-3" />
                Legal Requirements
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-300 leading-relaxed">
                Only Federal Firearms License (FFL) holders are permitted to legally transfer firearms across state lines. All transfers must comply with federal and state laws.
              </p>
              
              <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 mt-4">
                <h3 className="text-white font-semibold mb-4 text-lg">To receive a firearm at TayLord Arms, you must:</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Be <strong className="text-white">21 years of age or older</strong></span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">
                      Be a <strong className="text-white">South Carolina resident</strong> or <strong className="text-white">active-duty military</strong> stationed in South Carolina
                      <span className="block text-sm text-gray-400 mt-2 ml-0">
                        (Active-duty military must present a valid military ID and official orders showing assignment in South Carolina. Digital or printed copies are accepted.)
                      </span>
                    </span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Complete all <strong className="text-white">required federal paperwork and background checks</strong></span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-5 mt-6">
                <p className="text-gray-300 leading-relaxed">
                  <span className="text-white font-semibold">We are a Type 01 Federal Firearms License (FFL) Dealer,</span> authorized to handle standard firearm transfers.
                </p>
                <a 
                  href="https://www.atf.gov/firearms/firearms-guides-importation-verification-firearms-national-firearms-act-definitions" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-gray-300 hover:text-white transition-colors mt-4"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  <span className="text-sm">Learn more about federal firearm laws (ATF)</span>
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Online Firearm Purchase Transfers */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-2xl flex items-center">
                <FileText className="w-6 h-6 text-gray-300 mr-3" />
                FFL Transfers for Online Purchases
              </CardTitle>
              <CardDescription className="text-gray-400">
                Follow these steps when purchasing a firearm online
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { step: 1, text: "Purchase your firearm from a licensed online retailer" },
                  { step: 2, text: "Receive confirmation that your order has been paid in full" },
                  { step: 3, text: "Contact TayLord Arms with your name and order confirmation details" },
                  { step: 4, text: "We will coordinate with the seller and notify you when your firearm arrives" },
                  { step: 5, text: "Upon pickup, you must complete ATF Form 4473 and pass a background check" }
                ].map((item) => (
                  <div key={item.step} className="flex items-start p-4 bg-slate-900 rounded-lg border border-slate-700">
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold mr-4 flex-shrink-0">
                      {item.step}
                    </div>
                    <p className="text-gray-300 leading-relaxed pt-1">{item.text}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Private Party Transfers */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-2xl flex items-center">
                <FileText className="w-6 h-6 text-gray-300 mr-3" />
                Private Party FFL Transfers
              </CardTitle>
              <CardDescription className="text-gray-400">
                For firearm transfers between private individuals
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { step: 1, text: "Contact TayLord Arms before shipment to coordinate the transfer" },
                  { step: 2, text: "Ensure the firearm is shipped in compliance with federal law" },
                  { step: 3, text: "Once received, we will notify the buyer that the firearm is ready for pickup" },
                  { step: 4, text: "The buyer must complete ATF Form 4473 and pass a background check before transfer" }
                ].map((item) => (
                  <div key={item.step} className="flex items-start p-4 bg-slate-900 rounded-lg border border-slate-700">
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold mr-4 flex-shrink-0">
                      {item.step}
                    </div>
                    <p className="text-gray-300 leading-relaxed pt-1">{item.text}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-2xl">Additional Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-gray-300 mr-3 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-300">All transfers are subject to federal and state regulations</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-gray-300 mr-3 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-300">A transfer fee will apply (contact us for current pricing)</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-gray-300 mr-3 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-300">Valid government-issued ID is required at pickup</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-2xl flex items-center">
                <DollarSign className="w-6 h-6 text-gray-300 mr-3" />
                Transfer Pricing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-900 border border-slate-700 rounded-lg p-6">
                <div className="flex items-center justify-between flex-wrap gap-3 pb-4 mb-4 border-b border-slate-700">
                  <span className="text-white font-semibold">FFL Transfer Fee</span>
                  <span className="text-2xl font-bold text-red-400 tabular-nums">$30 <span className="text-sm font-normal text-gray-400">per firearm</span></span>
                </div>
                <p className="text-gray-300 text-sm">
                  Contact us for pricing on special transfer circumstances.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* FAQ */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-2xl flex items-center">
                <HelpCircle className="w-6 h-6 text-gray-300 mr-3" />
                Frequently Asked Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {FAQ_ITEMS.map((item) => (
                  <div
                    key={item.q}
                    className="bg-slate-900 border border-slate-700 rounded-lg p-5"
                  >
                    <h3 className="text-white font-semibold mb-2">{item.q}</h3>
                    <p className="text-gray-300 leading-relaxed text-sm">{item.a}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Contact CTA with Form */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-2xl text-center">Have Questions About Transfers?</CardTitle>
              <CardDescription className="text-gray-300 text-center">
                As part of our complete firearm service offerings, we proudly provide FFL transfers to customers throughout Moncks Corner, SC and surrounding areas. Contact us today to discuss your firearm transfer needs — our team is ready to guide you through the process.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-w-2xl mx-auto">
                {/* Embedded Contact Form with FFL category pre-selected */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="ffl-name" className="text-gray-300">
                      Full Name *
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <Input
                        id="ffl-name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="pl-10 bg-slate-900 border-slate-700 text-white focus:border-gray-400 focus:ring-gray-400"
                        placeholder="John Doe"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ffl-email" className="text-gray-300">
                      Email Address *
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <Input
                        id="ffl-email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="pl-10 bg-slate-900 border-slate-700 text-white focus:border-gray-400 focus:ring-gray-400"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ffl-phone" className="text-gray-300">
                      Phone Number
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <Input
                        id="ffl-phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="pl-10 bg-slate-900 border-slate-700 text-white focus:border-gray-400 focus:ring-gray-400"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-300">
                      Inquiry Type
                    </Label>
                    <div className="bg-slate-900 border border-slate-700 rounded-md px-4 py-3 text-gray-400">
                      FFL Transfer (Firearm Transfer Services)
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ffl-message" className="text-gray-300">
                      Message *
                    </Label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                      <Textarea
                        id="ffl-message"
                        name="message"
                        required
                        rows={5}
                        value={formData.message}
                        onChange={handleInputChange}
                        className="pl-10 bg-slate-900 border-slate-700 text-white focus:border-gray-400 focus:ring-gray-400 resize-none"
                        placeholder="Tell us about your firearm transfer needs..."
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 text-base transition-colors duration-200"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>

                  <div className="text-center">
                    <p className="text-gray-400 text-sm">Or call us directly at <a href={`tel:${businessInfo.phone}`} className="text-gray-300 hover:text-white font-medium">{businessInfo.phone}</a></p>
                  </div>
                </form>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FFL;
